/**
 * Mock business counterparties + deal templates для Business Demo skin
 * (Issue #20; extended Phase B / Issue #25 — 4th template B-NEW-1).
 * Pure-frontend data — никакого Gateway round-trip'а.
 *
 * Design notes:
 *
 *   - **Counterparties are LABELS only.** Реальные адреса на wire-уровне =
 *     env-derived `addrA` / `addrB` (через GET `/api/party-addresses`),
 *     потому что Gateway 4-tier validation требует pubkey↔party binding.
 *     Wizard mapps выбранный counterparty entity → один из 2 env addresses
 *     («Покупатель» → `addrA`, «Продавец» → `addrB`). UI пользователь видит
 *     красивые названия; backend получает корректные адреса.
 *
 *   - **Buyer / seller mock pool НЕ симметричен** — у buyer'ов и seller'ов
 *     разные mock-сценарии (ритейл vs B2B vs регулируемый сектор) — это
 *     даёт демо-впечатление «realistic business mix».
 *
 *   - **Deal templates** — 5 архетипов покрывают canonical Patent §8 deal
 *     types: immediate transfer (`/case`, 1 agreement, §8.1), scheduled
 *     transfer (`/case` scheduled, §8.2), composite loan-like (`/contract`
 *     principal + return, §8.3), composite delivery-with-prepayment +
 *     buyer-protection reverse obligation (`/contract`
 *     type=`delivery_with_prepayment`, §8.4 — Phase F B-NEW-2),
 *     composite delivery-against-payment (`/contract` both scheduled,
 *     satisfied via acts, §8.5 post-payment).
 *
 *   - **Russian-first labels.** Audience = бизнес-заказчики и регуляторы
 *     (см. Issue #20 «Цель»). Internationalisation за пределами scope.
 */

export interface MockCounterparty {
  /** Stable id для React keys + state. */
  readonly id: string;
  /** Display name (entity legal-style). */
  readonly name: string;
  /** Sub-label под именем (отрасль / роль). */
  readonly hint: string;
  /** `'buyer'` или `'seller'` — pool selection. */
  readonly side: 'buyer' | 'seller';
}

export interface DealTemplate {
  /** Stable id — also used to choose Gateway endpoint (`case_create` vs `contract_create`). */
  readonly id:
    | 'immediate-transfer'
    | 'gift'
    | 'scheduled-payment'
    | 'delivery-with-repayment'
    | 'delivery-with-prepayment-guarantee'
    | 'delivery-post-payment';
  /** Display title. */
  readonly title: string;
  /** Short user-facing description. */
  readonly summary: string;
  /** Indicative business-flow stages для UI explanation. */
  readonly stages: readonly string[];
  /** Which Gateway endpoint ultimately gets POSTed. */
  readonly gatewayEndpoint: 'case_create' | 'contract_create';
  /** Whether this template requires a future-dated `execute_at`. */
  readonly requiresDate: boolean;
}

export const MOCK_BUYERS: readonly MockCounterparty[] = [
  {
    id: 'buyer-romashka',
    name: 'ООО «Ромашка»',
    hint: 'Розничная сеть продуктовых магазинов',
    side: 'buyer',
  },
  {
    id: 'buyer-ip-ivanov',
    name: 'ИП Иванов А.В.',
    hint: 'Частный предприниматель, поставки кофе',
    side: 'buyer',
  },
  {
    id: 'buyer-techbuild',
    name: 'АО «ТехноСтрой»',
    hint: 'Строительный холдинг, B2B-закупки',
    side: 'buyer',
  },
  {
    id: 'buyer-medclinic',
    name: 'ООО «МедКлиник Плюс»',
    hint: 'Сеть медицинских центров',
    side: 'buyer',
  },
  {
    id: 'buyer-agrofarm',
    name: 'СПК «АгроФерма Заря»',
    hint: 'Сельхозкооператив, экспортные операции',
    side: 'buyer',
  },
];

export const MOCK_SELLERS: readonly MockCounterparty[] = [
  {
    id: 'seller-gazprom',
    name: 'АО «Газпромнефть»',
    hint: 'Поставка нефтепродуктов',
    side: 'seller',
  },
  {
    id: 'seller-logistex',
    name: 'ООО «ЛогистЭкс»',
    hint: 'Логистика и складские услуги',
    side: 'seller',
  },
  {
    id: 'seller-it-vendor',
    name: 'ООО «АйТи-Вендор»',
    hint: 'Корпоративный софт и лицензии',
    side: 'seller',
  },
  {
    id: 'seller-pharma',
    name: 'АО «ФармаДистрибьютор»',
    hint: 'Дистрибуция лекарственных средств',
    side: 'seller',
  },
  {
    id: 'seller-construction',
    name: 'ООО «СтройМонтаж-Север»',
    hint: 'Подрядные строительные работы',
    side: 'seller',
  },
];

export const DEAL_TEMPLATES: readonly DealTemplate[] = [
  {
    id: 'immediate-transfer',
    title: 'Перевод сейчас',
    summary:
      'Покупатель платит продавцу указанную сумму немедленно. Один шаг, фиксация в реестре сразу после подписания.',
    stages: [
      'Подписание Ed25519 от обеих сторон',
      'Передача в DLT.Gateway',
      'Запись в DLT — событие «Договор подписан, средства переведены»',
    ],
    gatewayEndpoint: 'case_create',
    requiresDate: false,
  },
  {
    id: 'gift',
    title: 'Дарение',
    summary:
      'Даритель безвозмездно передаёт получателю указанную сумму. Встречных обязательств нет — один немедленный перевод, фиксация в реестре сразу после подписания обеими сторонами. На wire это `type: "gift"` (тот же механизм, что у «Перевода сейчас», но иной бизнес-смысл — безвозмездность).',
    stages: [
      'Подписание Ed25519 от обеих сторон (даритель и получатель)',
      'Передача в DLT.Gateway',
      'Запись в DLT — событие «Договор подписан, средства переведены»',
    ],
    gatewayEndpoint: 'case_create',
    requiresDate: false,
  },
  {
    id: 'scheduled-payment',
    title: 'Платёж по сроку',
    summary:
      'Покупатель обязуется заплатить продавцу к определённой дате. До наступления даты обязательство видно в реестре как «ожидает исполнения».',
    stages: [
      'Подписание Ed25519 от обеих сторон',
      'Регистрация в DLT.Gateway с future-dated execute_at',
      'Ожидание исполнения — состояние «pending»',
      'Закрытие после фактического перевода средств',
    ],
    gatewayEndpoint: 'case_create',
    requiresDate: true,
  },
  {
    id: 'delivery-with-repayment',
    title: 'Поставка с двумя этапами',
    summary:
      'Сложная сделка: продавец получает аванс сразу + оставшуюся сумму после поставки. Контракт состоит из двух связанных обязательств.',
    stages: [
      'Подписание спецификации (основной платёж + оплата по поставке)',
      'Регистрация composite-контракта в DLT.Gateway',
      'Аванс перечислен → событие «Аванс выплачен»',
      'После поставки — финальный платёж и закрытие',
    ],
    gatewayEndpoint: 'contract_create',
    requiresDate: true,
  },
  {
    id: 'delivery-with-prepayment-guarantee',
    title: 'Поставка с предоплатой и гарантией возврата',
    summary:
      'Покупатель платит предоплату немедленно; продавец одновременно подписывает обратное обязательство — если поставка не зафиксирована к указанной дате, средства автоматически возвращаются покупателю. Два связанных соглашения, защита покупателя гарантирована реестром.',
    stages: [
      'Подписание спецификации (предоплата + обратное обязательство возврата)',
      'Регистрация composite-контракта в DLT.Gateway (type=delivery_with_prepayment)',
      'Предоплата перечислена немедленно → событие «Аванс выплачен»',
      'Фиксация поставки актом → закрытие обратного обязательства',
      'Если акт не зафиксирован к сроку — TimeKeeper триггерит возврат средств',
    ],
    gatewayEndpoint: 'contract_create',
    requiresDate: true,
  },
  {
    id: 'delivery-post-payment',
    title: 'Поставка с оплатой по факту',
    summary:
      'Продавец поставляет товар первым; оплата фиксируется только после подтверждения фактической поставки актом. Оба обязательства scheduled, реальное закрытие — через последовательность актов.',
    stages: [
      'Подписание спецификации (обязательство поставки + обязательство оплаты, оба к указанной дате)',
      'Регистрация composite-контракта в DLT.Gateway (type=delivery_against_act)',
      'Поставка зафиксирована актом → событие «Акт поставки принят»',
      'Оплата по факту — финальный платёж и закрытие контракта',
    ],
    gatewayEndpoint: 'contract_create',
    requiresDate: true,
  },
];

export function findCounterpartyById(id: string): MockCounterparty | undefined {
  return MOCK_BUYERS.find((c) => c.id === id) ?? MOCK_SELLERS.find((c) => c.id === id);
}

export function findDealTemplateById(id: string): DealTemplate | undefined {
  return DEAL_TEMPLATES.find((t) => t.id === id);
}
